import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { checkIpVpnStatus } from './services/vpnService';


// ============================================================================
// UTILITY: SHA-256 hash with pure-JS fallback
// ============================================================================
async function hashString(str) {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        try {
            const msgUint8 = new TextEncoder().encode(str);
            const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (e) {
            // Fallback to FNV-1a hash below
        }
    }
    // Pure JS 64-bit style hash fallback
    let h1 = 0x811c9dc5;
    let h2 = 0x9e3779b9;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 0x01000193);
        h2 = Math.imul(h2 ^ ch, 0x85ebca6b);
    }
    const hex1 = (h1 >>> 0).toString(16).padStart(8, '0');
    const hex2 = (h2 >>> 0).toString(16).padStart(8, '0');
    return `${hex1}${hex2}`;
}

// ============================================================================
// 1. MULTI-LAYER PERSISTENT DEVICE ID (localStorage + Cookie + IndexedDB)
// ============================================================================
const DEVICE_ID_KEY = 'app_device_uuid_v3';
const IDB_NAME = 'FingerprintStore';
const IDB_STORE = 'deviceIds';

function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/** Read/write UUID from IndexedDB */
function idbGet(key) {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open(IDB_NAME, 1);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(IDB_STORE)) {
                    db.createObjectStore(IDB_STORE);
                }
            };
            req.onsuccess = (e) => {
                try {
                    const db = e.target.result;
                    const tx = db.transaction(IDB_STORE, 'readonly');
                    const store = tx.objectStore(IDB_STORE);
                    const getReq = store.get(key);
                    getReq.onsuccess = () => resolve(getReq.result || null);
                    getReq.onerror = () => resolve(null);
                } catch (_) { resolve(null); }
            };
            req.onerror = () => resolve(null);
        } catch (_) { resolve(null); }
    });
}

function idbSet(key, value) {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open(IDB_NAME, 1);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(IDB_STORE)) {
                    db.createObjectStore(IDB_STORE);
                }
            };
            req.onsuccess = (e) => {
                try {
                    const db = e.target.result;
                    const tx = db.transaction(IDB_STORE, 'readwrite');
                    const store = tx.objectStore(IDB_STORE);
                    store.put(value, key);
                    tx.oncomplete = () => resolve(true);
                    tx.onerror = () => resolve(false);
                } catch (_) { resolve(false); }
            };
            req.onerror = () => resolve(false);
        } catch (_) { resolve(false); }
    });
}

function getCookieValue(key) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

function setCookieValue(key, value) {
    if (typeof document !== 'undefined') {
        document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=315360000; SameSite=Lax`;
    }
}

/**
 * Gets or generates a persistent device UUID stored across multiple layers.
 * Checks: localStorage → Cookie → IndexedDB → generate new.
 * Writes back to ALL layers for maximum persistence.
 */
async function getMultiLayerPersistentId() {
    try {
        // Try reading from all sources
        let id = null;

        // 1. localStorage
        try { id = localStorage.getItem(DEVICE_ID_KEY); } catch (_) {}

        // 2. Cookie
        if (!id) {
            id = getCookieValue(DEVICE_ID_KEY);
        }

        // 3. IndexedDB
        if (!id) {
            id = await idbGet(DEVICE_ID_KEY);
        }

        // 4. sessionStorage (grab from same session if available)
        if (!id) {
            try { id = sessionStorage.getItem(DEVICE_ID_KEY); } catch (_) {}
        }

        // Generate new if nothing found
        if (!id) {
            id = generateUUID();
        }

        // Write back to ALL layers
        try { localStorage.setItem(DEVICE_ID_KEY, id); } catch (_) {}
        setCookieValue(DEVICE_ID_KEY, id);
        await idbSet(DEVICE_ID_KEY, id);
        try { sessionStorage.setItem(DEVICE_ID_KEY, id); } catch (_) {}

        return id;
    } catch (e) {
        return 'fallback_id_' + Math.random().toString(36).substring(2);
    }
}

// ============================================================================
// 2. CANVAS FINGERPRINT (existing, improved)
// ============================================================================
function getCanvasFingerprint() {
    try {
        if (typeof document === 'undefined') return 'no_doc';
        const canvas = document.createElement('canvas');
        canvas.width = 280;
        canvas.height = 60;
        const ctx = canvas.getContext('2d');
        if (!ctx) return 'no_ctx';

        ctx.textBaseline = 'top';
        ctx.font = "14px 'Arial', 'Helvetica', sans-serif";
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);

        ctx.fillStyle = '#069';
        ctx.fillText('AntiFraudDetector 😃🔥 123!@#', 2, 15);

        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.font = "16px 'Times New Roman', serif";
        ctx.fillText('LinkTree Canvas Test', 4, 35);

        ctx.beginPath();
        ctx.arc(50, 50, 10, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();

        return canvas.toDataURL();
    } catch (e) {
        return 'canvas_error';
    }
}

// ============================================================================
// 3. WEBGL FINGERPRINT (extended with GPU parameters)
// ============================================================================
function getExtendedWebGLFingerprint() {
    try {
        if (typeof document === 'undefined') return { vendor: 'no_doc', renderer: 'no_doc', params: '' };
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return { vendor: 'no_webgl', renderer: 'no_webgl', params: '' };

        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
        const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);

        // Extended GPU parameters — these vary by GPU model and driver version
        const params = [
            gl.getParameter(gl.MAX_TEXTURE_SIZE),
            gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
            gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
            gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
            gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
            gl.getParameter(gl.MAX_VARYING_VECTORS),
            gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
            gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
            gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
            gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
            gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE)?.toString(),
            gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE)?.toString(),
            gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.toString(),
            gl.getParameter(gl.STENCIL_BITS),
            gl.getParameter(gl.DEPTH_BITS),
        ].join('|');

        // Supported extensions list
        const extensions = (gl.getSupportedExtensions() || []).sort().join(',');

        return {
            vendor: vendor || 'unknown_vendor',
            renderer: renderer || 'unknown_renderer',
            params,
            extensions,
        };
    } catch (e) {
        return { vendor: 'webgl_err', renderer: 'webgl_err', params: '', extensions: '' };
    }
}

// ============================================================================
// 4. AUDIO CONTEXT FINGERPRINT ⭐ (High uniqueness even on same model phones)
// ============================================================================
function getAudioFingerprint() {
    return new Promise((resolve) => {
        try {
            const AudioCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
            if (!AudioCtx) { resolve('no_audio_ctx'); return; }

            const context = new AudioCtx(1, 44100, 44100);
            const oscillator = context.createOscillator();
            oscillator.type = 'triangle';
            oscillator.frequency.setValueAtTime(10000, context.currentTime);

            const compressor = context.createDynamicsCompressor();
            compressor.threshold.setValueAtTime(-50, context.currentTime);
            compressor.knee.setValueAtTime(40, context.currentTime);
            compressor.ratio.setValueAtTime(12, context.currentTime);
            compressor.attack.setValueAtTime(0, context.currentTime);
            compressor.release.setValueAtTime(0.25, context.currentTime);

            oscillator.connect(compressor);
            compressor.connect(context.destination);
            oscillator.start(0);

            context.startRendering();
            context.oncomplete = (event) => {
                try {
                    const data = event.renderedBuffer.getChannelData(0);
                    // Sample a few specific positions to create the fingerprint
                    let result = 0;
                    for (let i = 4500; i < 5000; i++) {
                        result += Math.abs(data[i]);
                    }
                    resolve(result.toString());
                } catch (_) {
                    resolve('audio_process_err');
                }
            };

            // Timeout fallback
            setTimeout(() => resolve('audio_timeout'), 2000);
        } catch (e) {
            resolve('audio_error');
        }
    });
}

// ============================================================================
// 5. FONT DETECTION FINGERPRINT
// ============================================================================
function getFontFingerprint() {
    try {
        if (typeof document === 'undefined') return 'no_doc';

        const testFonts = [
            'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Georgia',
            'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Palatino Linotype',
            'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana',
            'Roboto', 'Droid Sans', 'Noto Sans', 'Samsung Sans',
            'Segoe UI', 'Helvetica Neue', 'Fira Sans', 'Ubuntu',
            'Cantarell', 'DejaVu Sans', 'Liberation Sans', 'Oxygen',
            'Lato', 'Open Sans', 'Source Sans Pro', 'Montserrat', 'Poppins',
        ];

        const baseFonts = ['monospace', 'sans-serif', 'serif'];
        const testString = 'mmMwWLliI0OoQ1qzZaAbBdDgG!@#$%&';
        const testSize = '72px';

        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (!ctx) return 'no_ctx';

        // Measure base font widths
        const baseWidths = {};
        for (const base of baseFonts) {
            ctx.font = `${testSize} ${base}`;
            baseWidths[base] = ctx.measureText(testString).width;
        }

        // Check which test fonts are installed by comparing width against base
        const detected = [];
        for (const font of testFonts) {
            let isDetected = false;
            for (const base of baseFonts) {
                ctx.font = `${testSize} '${font}', ${base}`;
                const width = ctx.measureText(testString).width;
                if (width !== baseWidths[base]) {
                    isDetected = true;
                    break;
                }
            }
            if (isDetected) detected.push(font);
        }

        return detected.join(',');
    } catch (e) {
        return 'font_error';
    }
}

// ============================================================================
// 6. MATH FINGERPRINT (FPU implementation differences)
// ============================================================================
function getMathFingerprint() {
    try {
        const results = [
            Math.tan(-1e300),
            Math.atan2(1, 0),
            Math.sinh(1),
            Math.cosh(1),
            Math.tanh(0.5),
            Math.expm1(1),
            Math.log1p(0.5),
            Math.cbrt(2),
            Math.log10(2),
            Math.log2(3),
            Math.fround(0.1 + 0.2),
            // These can differ across JS engines / hardware FPUs
            Math.acos(0.123456789),
            Math.asin(0.123456789),
            Math.atanh(0.5),
            Math.hypot(3, 4, 5),
            // Power operations
            Math.pow(Math.PI, -100),
            Math.pow(2, -1074),
        ];
        return results.map(v => v.toString()).join('|');
    } catch (e) {
        return 'math_error';
    }
}

// ============================================================================
// 7. MEDIA DEVICES FINGERPRINT (camera/mic count varies per device)
// ============================================================================
async function getMediaDevicesFingerprint() {
    try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            return 'no_media_api';
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const counts = {
            audioinput: 0,
            audiooutput: 0,
            videoinput: 0,
        };
        for (const d of devices) {
            if (counts[d.kind] !== undefined) counts[d.kind]++;
        }
        // Group IDs (hashed) also vary per browser installation
        const groupIds = [...new Set(devices.map(d => d.groupId).filter(Boolean))];
        return `ai${counts.audioinput}ao${counts.audiooutput}vi${counts.videoinput}g${groupIds.length}`;
    } catch (e) {
        return 'media_error';
    }
}

// ============================================================================
// 8. ADDITIONAL HARDWARE & BROWSER SIGNALS
// ============================================================================
function getAdditionalSignals() {
    const signals = {};
    try {
        // Screen orientation
        if (window.screen && window.screen.orientation) {
            signals.orientationType = window.screen.orientation.type || '';
        }
        // Device pixel ratio at fractional precision
        signals.dpr = window.devicePixelRatio || 1;
        // Inner/outer window size ratio (can reveal browser chrome differences)
        signals.windowOuter = `${window.outerWidth}x${window.outerHeight}`;
        signals.windowInner = `${window.innerWidth}x${window.innerHeight}`;
        // Color gamut support
        signals.p3 = window.matchMedia('(color-gamut: p3)').matches;
        signals.srgb = window.matchMedia('(color-gamut: srgb)').matches;
        signals.rec2020 = window.matchMedia('(color-gamut: rec2020)').matches;
        // HDR
        signals.hdr = window.matchMedia('(dynamic-range: high)').matches;
        // Reduced motion preference
        signals.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Dark mode preference
        signals.darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        // Pointer type (coarse = touch, fine = mouse)
        signals.pointer = window.matchMedia('(pointer: coarse)').matches ? 'coarse' : 'fine';
        // Inverted colors
        signals.invertedColors = window.matchMedia('(inverted-colors: inverted)').matches;
        // PDF viewer
        signals.pdfViewer = navigator.pdfViewerEnabled !== undefined ? navigator.pdfViewerEnabled : 'unknown';
        // Vibrate support (most phones have it, desktops don't)
        signals.vibrate = typeof navigator.vibrate === 'function';
        // Do not track
        signals.doNotTrack = navigator.doNotTrack || '';
        // Cookies enabled
        signals.cookieEnabled = navigator.cookieEnabled;
        // Max touch points
        signals.maxTouchPoints = navigator.maxTouchPoints || 0;
    } catch (_) {}
    return signals;
}

// ============================================================================
// PRIMARY EXPORT: Enhanced Fingerprint (deterministic hash)
// ============================================================================

/**
 * Primary Enhanced Fingerprint function.
 * Combines: FingerprintJS + Persistent UUID + Canvas + WebGL Extended + AudioContext
 * + Font Detection + Math FPU + Media Devices + Hardware/Browser signals.
 *
 * The resulting hash is deterministic for the same device/browser combination.
 */
export const getEnhancedFingerprint = async () => {
    try {
        // 1. Base FingerprintJS ID (with 1.5s fallback race)
        let fpjsId = 'fpjs_none';
        try {
            const fpjsPromise = FingerprintJS.load().then(agent => agent.get());
            const timeoutPromise = new Promise(resolve => setTimeout(() => resolve(null), 1500));
            const result = await Promise.race([fpjsPromise, timeoutPromise]);
            if (result && result.visitorId) {
                fpjsId = result.visitorId;
            }
        } catch (e) {
            console.warn('FingerprintJS fallback:', e);
        }

        // 2. Multi-layer Persistent Device UUID
        const deviceUuid = await getMultiLayerPersistentId();

        // 3. Timezone & Localization
        let timezone = '';
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch (e) { }
        const tzOffset = new Date().getTimezoneOffset();
        const language = navigator.language || '';
        const languages = (navigator.languages || []).join(',');

        // 4. Hardware & Display Specs
        const screenRes = typeof window !== 'undefined' && window.screen ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}` : '';
        const availScreen = typeof window !== 'undefined' && window.screen ? `${window.screen.availWidth}x${window.screen.availHeight}` : '';
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 0 : 0;
        const memory = typeof navigator !== 'undefined' ? navigator.deviceMemory || 0 : 0;
        const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0;
        const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

        // 5. Canvas Fingerprint
        const canvasFp = getCanvasFingerprint();

        // 6. Extended WebGL Fingerprint (vendor + renderer + GPU params + extensions)
        const webglFp = getExtendedWebGLFingerprint();

        // 7. AudioContext Fingerprint ⭐
        const audioFp = await getAudioFingerprint();

        // 8. Font Detection Fingerprint
        const fontFp = getFontFingerprint();

        // 9. Math/FPU Fingerprint
        const mathFp = getMathFingerprint();

        // 10. Media Devices Fingerprint (camera/mic counts)
        const mediaFp = await getMediaDevicesFingerprint();

        // 11. Additional hardware/browser signals
        const additionalSignals = getAdditionalSignals();

        // 12. WebRTC local IPs — CLAVE ANTI-VPN ──────────────────────────────────
        //
        // Las IPs locales (192.168.x.x, 10.x.x.x) las asigna el router/DHCP
        // de la casa y NO cambian aunque el usuario use una VPN. Son el
        // identificador más estable disponible desde el browser.
        //
        // Incluyendo estas IPs en el hash, dos sesiones del mismo dispositivo
        // (aunque con distinta IP pública por VPN) producirán el MISMO fingerprint.
        let webrtcLocalIps = [];
        try {
            const allIps = await getWebRTCIPs();
            const privateRanges = [
                /^10\./,
                /^172\.(1[6-9]|2\d|3[01])\./,
                /^192\.168\./,
                /^169\.254\./,
            ];
            // Solo IPs privadas (las públicas pueden cambiar con VPN)
            webrtcLocalIps = allIps
                .filter(ip => privateRanges.some(r => r.test(ip)))
                .sort(); // Ordenadas para que el hash sea determinista
        } catch (_) {}

        // High-entropy payload — everything deterministic goes into the hash
        const rawSignature = JSON.stringify({
            deviceUuid,
            fpjsId,
            timezone,
            tzOffset,
            language,
            languages,
            screenRes,
            availScreen,
            dpr,
            cores,
            memory,
            maxTouchPoints,
            platform,
            userAgent,
            // Canvas
            canvasFp,
            // WebGL extended
            webglVendor: webglFp.vendor,
            webglRenderer: webglFp.renderer,
            webglParams: webglFp.params,
            webglExtensions: webglFp.extensions,
            // Audio
            audioFp,
            // Fonts
            fontFp,
            // Math/FPU
            mathFp,
            // Media devices
            mediaFp,
            // ⚡ WebRTC local IPs (anti-VPN — no cambian con VPN)
            webrtcLocalIps,
            // Additional signals
            ...additionalSignals,
        });

        const hash = await hashString(rawSignature);
        return hash;
    } catch (error) {
        console.error('Error generating enhanced fingerprint:', error);
        // Fallback: try to at least get persistent ID
        try {
            return await getMultiLayerPersistentId();
        } catch (_) {
            return 'fallback_id_' + Math.random().toString(36).substring(2);
        }
    }
};

/**
 * Returns the fingerprint hash PLUS the raw WebRTC local IPs.
 * Use this instead of getEnhancedFingerprint() when you want to also
 * send the local IPs to the backend for extra VPN correlation.
 *
 * Usage in Bonos.jsx:
 *   const { fingerprint, webrtcLocalIps } = await getEnhancedFingerprintWithMeta();
 *   // send both to backend
 */
export const getEnhancedFingerprintWithMeta = async () => {
    try {
        let webrtcLocalIps = [];
        let webrtcPublicIps = [];
        let vpnSignals = null;

        try {
            vpnSignals = await detectVPNSignals();
            webrtcLocalIps = vpnSignals.localIPs || [];
            webrtcPublicIps = vpnSignals.publicIPs || [];
        } catch (_) {}

        const fingerprint = await getEnhancedFingerprint();
        const vpnRiskScore = vpnSignals?.vpnRiskScore || 0;

        return {
            fingerprint,
            webrtcLocalIps,
            webrtcPublicIps,
            vpnSignals,
            // Solo marcar VPN como sospechoso si el score de riesgo es realmente elevado (>= 45)
            vpnSuspected: vpnRiskScore >= 45,
        };
    } catch (_) {
        return {
            fingerprint: await getEnhancedFingerprint().catch(() => 'error'),
            webrtcLocalIps: [],
            webrtcPublicIps: [],
            vpnSignals: null,
            vpnSuspected: false,
        };
    }
};

// ============================================================================
// VPN / PROXY DETECTION SIGNALS
// ============================================================================

/**
 * Detects potential VPN/proxy usage using client-side signals.
 * Returns an object with multiple indicators and a composite risk score (0-100).
 *
 * Signals used:
 * 1. WebRTC IP leak  — reveals real local/STUN IP even behind VPN
 * 2. Timezone vs browser language mismatch
 * 3. WebGL software renderer (common in VPN browser extensions)
 * 4. Headless / automation browser signals
 * 5. Unusual screen / window ratio (datacenter / VM screens)
 * 6. Navigator plugins count (VPN/headless browsers often have 0)
 */
async function detectVPNSignals() {
    const signals = {
        webrtcIPs: [],          // All IPs found via WebRTC
        localIPs: [],           // RFC-1918 private IPs
        publicIPs: [],          // Public IPs (different from visible IP = VPN leak)
        timezoneMismatch: false,
        softwareRenderer: false,
        headlessBrowser: false,
        suspiciousScreen: false,
        noPlugins: false,
        apiVpnDetected: false,
        ipDetails: null,
        vpnRiskScore: 0,        // 0 = clean, 100 = definitely VPN
    };

    let riskPoints = 0;

    // ── 0. API IP Proxy / Hosting / Datacenter Check ─────────────────────────
    try {
        const ipStatus = await checkIpVpnStatus();
        signals.ipDetails = ipStatus;
        if (ipStatus.isVpn || ipStatus.isProxy || ipStatus.isHosting) {
            signals.apiVpnDetected = true;
            riskPoints += ipStatus.vpnRiskScore || 50;
        }
    } catch (_) {}


    // ── 1. WebRTC IP Leak ────────────────────────────────────────────────────
    try {
        const ips = await getWebRTCIPs();
        signals.webrtcIPs = ips;
        const privateRanges = [
            /^10\./,
            /^172\.(1[6-9]|2\d|3[01])\./,
            /^192\.168\./,
            /^169\.254\./,
            /^fc00:/i,
            /^fd/i,
        ];
        for (const ip of ips) {
            const isPrivate = privateRanges.some(r => r.test(ip));
            if (isPrivate) {
                signals.localIPs.push(ip);
            } else if (!ip.includes(':')) {
                // Public IPv4 — could differ from the IP the server sees
                signals.publicIPs.push(ip);
            }
        }
        // Having a public WebRTC IP different from the page-request IP strongly
        // suggests a VPN or proxy is in use (the WebRTC stack bypasses it).
        if (signals.publicIPs.length > 0) {
            riskPoints += 35; // High signal
        }
        // Multiple local IPs can indicate VPN tunnel interfaces
        if (signals.localIPs.length > 2) {
            riskPoints += 15;
        }
    } catch (_) {}

    // ── 2. Timezone vs Accept-Language mismatch ──────────────────────────────
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const lang = (navigator.language || '').toLowerCase();

        // Simple heuristic: language region vs timezone continent
        // e.g. timezone = 'America/New_York' but lang = 'ar' (Arabic) → suspicious
        const tzContinent = timezone.split('/')[0];
        const CONTINENT_LANG_MAP = {
            'America': ['en', 'es', 'pt', 'fr', 'ht'],
            'Europe':  ['en', 'de', 'fr', 'it', 'es', 'pt', 'nl', 'pl', 'sv', 'ru', 'uk', 'cs', 'ro', 'hu', 'el', 'tr'],
            'Asia':    ['zh', 'ja', 'ko', 'hi', 'ar', 'th', 'vi', 'id', 'ms', 'tl', 'ur', 'bn', 'ta', 'te', 'fa', 'he', 'tr'],
            'Africa':  ['ar', 'sw', 'fr', 'pt', 'am', 'yo', 'ig', 'ha', 'zu'],
            'Pacific': ['en', 'tl', 'mi'],
            'Australia': ['en'],
            'Atlantic': ['en', 'pt'],
            'Indian':  ['en', 'fr'],
            'Arctic':  ['no', 'ru', 'da'],
            'Antarctica': ['en'],
        };
        const allowedLangs = CONTINENT_LANG_MAP[tzContinent] || [];
        const langCode = lang.split('-')[0];
        if (tzContinent && allowedLangs.length && !allowedLangs.includes(langCode)) {
            signals.timezoneMismatch = true;
            riskPoints += 20;
        }
    } catch (_) {}

    // ── 3. WebGL Software Renderer ───────────────────────────────────────────
    try {
        const webgl = getExtendedWebGLFingerprint();
        const renderer = (webgl.renderer || '').toLowerCase();
        const SOFTWARE_RENDERERS = [
            'swiftshader', 'llvmpipe', 'softpipe', 'vmware', 'virtualbox',
            'parallels', 'microsoft basic render', 'angle (google', 'mesa'
        ];
        if (SOFTWARE_RENDERERS.some(s => renderer.includes(s))) {
            signals.softwareRenderer = true;
            riskPoints += 25; // VM / container environment
        }
    } catch (_) {}

    // ── 4. Headless / Automation Browser ────────────────────────────────────
    try {
        const ua = (navigator.userAgent || '').toLowerCase();
        const isHeadless =
            /headlesschrome|headless/.test(ua) ||
            navigator.webdriver === true ||
            !window.chrome && /chrome/.test(ua) ||
            typeof window.__nightmare !== 'undefined' ||
            typeof window._phantom !== 'undefined' ||
            typeof window.callPhantom !== 'undefined';

        if (isHeadless) {
            signals.headlessBrowser = true;
            riskPoints += 30;
        }
    } catch (_) {}

    // ── 5. Suspicious Screen (datacenter/VM monitors) ────────────────────────
    try {
        const sw = window.screen.width;
        const sh = window.screen.height;
        // Common VM / remote-desktop resolutions with no taskbar / toolbar gaps
        const availW = window.screen.availWidth;
        const availH = window.screen.availHeight;
        // If available == total (no OS chrome), very suspicious on Windows/Mac
        const noOSChrome = sw === availW && sh === availH;
        // Very unusual resolutions often indicate a VM
        const unusualRes = (sw === 800 && sh === 600) ||
                           (sw === 1024 && sh === 768) ||
                           (sw === 1280 && sh === 800 && availH === 800);
        if (noOSChrome || unusualRes) {
            signals.suspiciousScreen = true;
            riskPoints += 10;
        }
    } catch (_) {}

    // ── 6. No Plugins (headless / stripped browsers) ─────────────────────────
    try {
        const pluginCount = navigator.plugins ? navigator.plugins.length : -1;
        if (pluginCount === 0) {
            signals.noPlugins = true;
            riskPoints += 10;
        }
    } catch (_) {}

    // Cap risk score at 100
    signals.vpnRiskScore = Math.min(100, riskPoints);
    return signals;
}

/**
 * Uses WebRTC to discover all local and STUN-resolved IP addresses.
 * These bypass VPN tunnels and reveal the real device network interfaces.
 */
function getWebRTCIPs() {
    return new Promise((resolve) => {
        const ips = new Set();
        const timeout = setTimeout(() => resolve([...ips]), 2000);

        try {
            // Works on Chrome, Firefox, Safari (where available)
            const RTCPeerConnection =
                window.RTCPeerConnection ||
                window.webkitRTCPeerConnection ||
                window.mozRTCPeerConnection;

            if (!RTCPeerConnection) {
                clearTimeout(timeout);
                return resolve([]);
            }

            const pc = new RTCPeerConnection({
                iceServers: [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                ],
            });

            pc.createDataChannel('');

            pc.onicecandidate = (event) => {
                if (!event || !event.candidate) {
                    // ICE gathering done
                    pc.close();
                    clearTimeout(timeout);
                    return resolve([...ips]);
                }
                const candidate = event.candidate.candidate;
                // Extract IP from SDP candidate string
                const matches = candidate.matchAll(
                    /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|[a-f0-9:]{2,}(?::[a-f0-9:]+)+)/gi
                );
                for (const m of matches) {
                    const ip = m[1];
                    // Skip loopback and multicast
                    if (!ip.startsWith('127.') && !ip.startsWith('0.') && ip !== '::1') {
                        ips.add(ip);
                    }
                }
            };

            pc.createOffer()
                .then(offer => pc.setLocalDescription(offer))
                .catch(() => { clearTimeout(timeout); resolve([...ips]); });
        } catch (_) {
            clearTimeout(timeout);
            resolve([]);
        }
    });
}

// ============================================================================
// SECONDARY EXPORT: Device Metadata (non-deterministic, for backend correlation)
// ============================================================================

/**
 * Returns volatile metadata about the device that should NOT be part of the hash
 * but is useful for backend correlation analysis.
 * 
 * Example: if two "different users" submit requests within 1 minute and both have
 * battery at 73% charging=false, they're likely the same device.
 */
export const getDeviceMetadata = async () => {
    const metadata = {};

    // Battery info
    try {
        if (navigator.getBattery) {
            const battery = await navigator.getBattery();
            metadata.batteryLevel = Math.round(battery.level * 100);
            metadata.batteryCharging = battery.charging;
        }
    } catch (_) {}

    // Network info
    try {
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (conn) {
            metadata.connectionType = conn.effectiveType || '';
            metadata.downlink = conn.downlink || '';
            metadata.rtt = conn.rtt || '';
            metadata.saveData = conn.saveData || false;
        }
    } catch (_) {}

    // Screen brightness proxy (via ambient light if available — rare but useful)
    try {
        metadata.screenOrientation = (window.screen && window.screen.orientation) ? window.screen.orientation.type : '';
    } catch (_) {}

    // VPN / Proxy detection signals
    try {
        const vpnSignals = await detectVPNSignals();
        metadata.vpnSignals = vpnSignals;
    } catch (_) {}

    // Timestamp of collection
    metadata.collectedAt = new Date().toISOString();

    return metadata;
};

// ============================================================================
// TERTIARY EXPORT: Standalone VPN/Proxy Detection
// ============================================================================

/**
 * Standalone export for VPN/proxy detection.
 * Use this when you only need the risk assessment without the full metadata.
 *
 * Returns:
 *   - vpnRiskScore: 0-100 (0 = clean, ≥50 = likely VPN, ≥70 = very likely VPN)
 *   - webrtcIPs: all IPs discovered via WebRTC (bypass VPN)
 *   - localIPs: private-range IPs found
 *   - publicIPs: public IPs found via WebRTC (may differ from server-seen IP)
 *   - timezoneMismatch: browser language inconsistent with timezone
 *   - softwareRenderer: WebGL software/VM renderer detected
 *   - headlessBrowser: automation / headless browser detected
 *   - suspiciousScreen: unusual screen resolution (VM / RDP)
 *   - noPlugins: browser has zero plugins (headless signal)
 *
 * Usage in backend:
 *   const { vpnRiskScore, publicIPs } = await getVPNSignals();
 *   if (vpnRiskScore >= 35) flag user for manual review;
 */
export const getVPNSignals = async () => {
    return await detectVPNSignals();
};
